import type { AxiosProgressEvent } from 'axios'

import { useStore } from '@nanostores/react'
import { httpRequest } from 'api/httpRequest'
import { CONSTS } from 'config/objectConst'
import { appConnector } from 'shared/connector'
import { shouldUseRegularFetch } from 'shared/connector/rtk-adapt.ts'
import { notifyError } from 'shared/errors'

import type { AttachResponse } from '../../model/types'

import { useCurrentId } from '../../hooks'
import * as files from '../../utils/files'
import {
    $reUploadMap,
    addReUpload,
    checkSizeLimit,
    Error as ErrorMessage,
    removeBase64Header,
    toBase64
} from './utils'

const useGetReupload = (id: string) => useStore($reUploadMap, { keys: [id] })

async function uploadExternalMedia({
  conversation,
  id,
  url
}: { conversation: string; id: string; url: string; }) {
  try {
    files.add({
      error: '',
      id,
      loaded: 0,
      name: 'GIF',
      preview: url,
      size: 100,
			type: 'image/gif'
    })

    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch GIF: ${response.statusText}`)
    }
    const blob = await response.blob()
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(new Error('Failed to convert GIF to Base64'))
      reader.readAsDataURL(blob)
    })

    const data = {
      attachmentType: 'ExternalMedia',
			content: removeBase64Header(base64),
			contentType: 'image/gif',
			fileName: 'GIF'
    }

    if (shouldUseRegularFetch()) {
      const response = await httpRequest<AttachResponse>({
        data,
        method: 'POST',
        url: `${CONSTS.API.URL_AUTOMATION}conversations/${conversation}/attachments`
      })

      const attach = response?.data
      if (attach && (response?.status === 200 || response?.status === 201)) {
        files.saveAttach({ attach, id })
      } else if (attach) {
       files.saveAttach({ attach, id })
      }
    } else {
      const attach = await appConnector.linkedIn.conversations.uploadConversationAttachment(
        conversation,
        data
      )
			console.log(attach)
        files.saveAttach({ attach: attach as AttachResponse, id })
    }
  } catch (error) {
    notifyError(error)
    files.setError({ error: ErrorMessage.Upload, id })
  }
}

async function uploadFile({
	conversation,
	file,
	id
}: { conversation: string; file: File; id: string }) {
	const isImage = file.type.startsWith('image/')

	try {
		const preview = await toBase64(file)

		files.add({
			error: '',
			id,
			loaded: 0,
			name: file.name,
			preview: isImage ? preview : '',
			size: file.size,
			type: file.type
		})

		if (!checkSizeLimit(file)) {
			notifyError(new Error(ErrorMessage.Size))
			return files.remove(id)
		}

		const attachmentType = isImage ? 'VectorImage' : 'File'
		const data = {
			attachmentType,
			content: removeBase64Header(preview),
			contentType: file.type,
			fileName: file.name
		}

		if (shouldUseRegularFetch()) {
			const response = await httpRequest<AttachResponse>({
				data,
				method: 'POST',

				onUploadProgress(event: AxiosProgressEvent) {
					const percent =
						event.progress ?? event.loaded / (event.total ?? file.size)
					files.updateLoaded({
						id,
						loaded: percent * 100
					})
				},
				url: `${CONSTS.API.URL_AUTOMATION}conversations/${conversation}/attachments`
			})

			if (response?.status === 200) {
				files.saveAttach({
					attach: response.data,
					id
				})
			}
		} else {
			const attach =
				await appConnector.linkedIn.conversations.uploadConversationAttachment(
					conversation,
					data
				)

			console.log(attach)

			files.saveAttach({
				attach,
				id
			})
		}
	} catch (error) {
		addReUpload(id, () =>
			uploadFile({
				conversation,
				file,
				id
			})
		)

		files.setError({ error: ErrorMessage.Upload, id })
	}
}

function useUploadExternalMedia() {
  const conversation = useCurrentId()

  return (url: string) => {
    const id = crypto.randomUUID()
    return uploadExternalMedia({ conversation, id, url })
  }
}

function useUploadFile() {
	const conversation = useCurrentId()

	return (file: File) => {
		const id = crypto.randomUUID()

		return uploadFile({
			conversation,
			file,
			id
		})
	}
}

export { useGetReupload, useUploadExternalMedia, useUploadFile }
